import { useIntl } from 'react-intl';
import type { UserAppDraft } from '@api/appBuilder';
import { axiosErrorMessage } from '@api/errors';
import useSnackbar from '@hooks/useSnackbar';
import { useUserAppVersions } from '../useAppBuilder';
import { usePublishFlow } from '../usePublishFlow';

export const useBuilderPublishing = (
  draft: UserAppDraft,
  onReload: () => void,
  onProblems: (problems: readonly string[]) => void,
) => {
  const intl = useIntl();
  const { showError, showSuccess } = useSnackbar();
  const versions = useUserAppVersions(draft.appKey, draft.publishedVersion != null);

  const failed = (err: unknown) =>
    showError(
      axiosErrorMessage(
        err,
        intl.formatMessage({
          id: 'apps.builder.error.action',
          defaultMessage: 'That did not work. Reload the page and try again.',
        }),
      ),
    );

  const flow = usePublishFlow({
    draft,
    onReload,
    onConflict: () =>
      showError(
        intl.formatMessage({
          id: 'apps.builder.publish.conflict',
          defaultMessage: 'This app was changed somewhere else. The latest version is loaded; check it and try again.',
        }),
      ),
    onProblems,
    onFailed: failed,
    onPublished: (version) =>
      showSuccess(intl.formatMessage({ id: 'apps.builder.publish.done', defaultMessage: 'Published as v{version}.' }, { version })),
    onUnchanged: (version) =>
      showSuccess(
        intl.formatMessage(
          { id: 'apps.builder.publish.unchanged', defaultMessage: 'Nothing new to publish: v{version} is already live.' },
          { version },
        ),
      ),
    onRestored: (version) =>
      showSuccess(
        version.live
          ? intl.formatMessage(
              { id: 'apps.builder.restore.live', defaultMessage: 'Your draft is back to v{version}.' },
              { version: version.version },
            )
          : intl.formatMessage(
              { id: 'apps.builder.restore.done', defaultMessage: 'v{version} is in your draft. Publish it to make it live.' },
              { version: version.version },
            ),
      ),
  });

  return { ...flow, versions: versions.data ?? [] };
};
